import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserEdit } from "@fortawesome/free-solid-svg-icons";
import { usersApi } from "../api/usersApi";
import { ApiError } from "../api/apiError";

const EditUser: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] =
  useState({
    fullName: "",
    email: "",
    phoneNumber: "",
  });

const [loading, setLoading] =
  useState(true);

const [saving, setSaving] =
  useState(false);


  useEffect(() => {
  const fetchUserData =
    async () => {
      if (!id) {
        toast.error(
          "معرف المستخدم غير موجود",
        );

        navigate(
          "/dashboard/users",
        );

        return;
      }

      try {
        setLoading(true);

        const result =
          await usersApi.getById(
            id,
          );

        setFormData({
          fullName:
            result.user.fullName ||
            "",

          email:
            result.user.email ||
            "",

          phoneNumber:
            result.user.phoneNumber ||
            "",
        });
      } catch (error) {
        console.error(
          "Error fetching user:",
          error,
        );

        if (
          error instanceof ApiError
        ) {
          if (
            error.status === 403
          ) {
            toast.error(
              "لا تمتلك صلاحية عرض المستخدم",
            );

            navigate(
              "/dashboard/users",
            );

            return;
          }

          if (
            error.status === 404
          ) {
            toast.error(
              "الحساب غير موجود",
            );

            navigate(
              "/dashboard/users",
            );

            return;
          }

          toast.error(
            error.message ||
              "حدث خطأ أثناء جلب بيانات المستخدم",
          );

          return;
        }

        toast.error(
          "تعذر الاتصال بالخادم",
        );
      } finally {
        setLoading(false);
      }
    };

  fetchUserData();
}, [id, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleSubmit = async (
  e: React.FormEvent,
) => {
  e.preventDefault();

  if (!id || saving) {
    return;
  }

  const fullName =
    formData.fullName.trim();

  const email =
    formData.email
      .trim()
      .toLowerCase();

  const phoneNumber =
    formData.phoneNumber.trim();

  if (
    fullName.length < 2
  ) {
    toast.error(
      "يرجى إدخال اسم صحيح",
    );

    return;
  }

  if (!email) {
    toast.error(
      "يرجى إدخال البريد الإلكتروني",
    );

    return;
  }

  try {
    setSaving(true);

    await usersApi.update(
      id,
      {
        fullName,

        email,

        phoneNumber:
          phoneNumber || null,
      },
    );

    toast.success(
      "تم تحديث البيانات بنجاح!",
    );

    navigate(
      "/dashboard/users",
    );
  } catch (error) {
    console.error(
      "Error updating user:",
      error,
    );

    if (
      error instanceof ApiError
    ) {
      if (
        error.status === 400
      ) {
        toast.error(
          error.message ||
            "بيانات المستخدم غير صحيحة",
        );

        return;
      }

      if (
        error.status === 403
      ) {
        toast.error(
          "لا تمتلك صلاحية تعديل المستخدم",
        );

        return;
      }

      if (
        error.status === 404
      ) {
        toast.error(
          "المستخدم غير موجود",
        );

        return;
      }

      if (
        error.status === 409
      ) {
        toast.error(
          error.message ||
            "البريد الإلكتروني أو رقم الهاتف مستخدم مسبقًا",
        );

        return;
      }

      toast.error(
        error.message ||
          "حدث خطأ أثناء حفظ البيانات",
      );

      return;
    }

    toast.error(
      "تعذر الاتصال بالخادم",
    );
  } finally {
    setSaving(false);
  }
};

if (loading) {
  return (
    <div className="w-full p-6 text-center">
      جاري تحميل بيانات المستخدم...
    </div>
  );
}

  return (
    <div className="w-full mx-auto bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="max-w-full flex items-center justify-between bg-gradient-to-r from-[#197184] to-[#269BB4] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl font-bold flex items-center gap-2"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          <FontAwesomeIcon icon={faUserEdit} />
          تعديل حساب المستخدم
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
  {
    name: "fullName",
    label: "الاسم الكامل",
  },
  {
    name: "email",
    label: "البريد الإلكتروني",
  },
  {
    name: "phoneNumber",
    label: "رقم الهاتف",
  },
].map(({ name, label }) => (
            <div key={name}>
              <label className="block text-gray-600 font-semibold mb-1 font-custom">
                {label}:
              </label>
              <input
                type="text"
                name={name}
                value={(formData as any)[name]}
                onChange={handleChange}
                placeholder={label}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-custom"
                required
              />
            </div>
          ))}
        </div>

        <div className="text-left mt-4">
          <button
  type="submit"
  disabled={saving}
  className="bg-[#197184] hover:bg-[#155c6e] disabled:opacity-60 text-white py-2 px-6 rounded-md shadow-md transition"
>
  {saving
    ? "جاري الحفظ..."
    : "حفظ التغييرات"}
</button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
